import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart3, TrendingUp, DollarSign, Activity } from "lucide-react";
import { OverviewTab } from "./tabs/OverviewTab";
import { useTranslation } from "react-i18next";
import { lazy, Suspense, useEffect, useMemo, useRef } from "react";
import { useDashboardConfigStore } from "@/store/dashboardConfigStore";
import { DashboardTab } from "@/config/dashboardWidgets";
import { useDashboardPeriodStore } from "@/store/dashboardPeriodStore";
import { useUserStore } from "@/store/userStore";

// Lazy load non-critical tabs
const PerformanceTab = lazy(() => import("./tabs/PerformanceTab").then(m => ({ default: m.PerformanceTab })));
const FinancesTab = lazy(() => import("./tabs/FinancesTab").then(m => ({ default: m.FinancesTab })));
const ActivityTab = lazy(() => import("./tabs/ActivityTab").then(m => ({ default: m.ActivityTab })));

// Fallback component for lazy loaded tabs
function TabLoading() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

interface TabDefinition {
  id: DashboardTab;
  icon: typeof BarChart3;
  labelKey: string;
  shortLabelKey: string;
}

const ALL_TABS: TabDefinition[] = [
  { id: 'overview', icon: BarChart3, labelKey: "tabs.overview", shortLabelKey: "tabs.shortOverview" },
  { id: 'performance', icon: TrendingUp, labelKey: "tabs.performance", shortLabelKey: "tabs.shortPerformance" },
  { id: 'finances', icon: DollarSign, labelKey: "tabs.finances", shortLabelKey: "tabs.shortFinances" },
  { id: 'activity', icon: Activity, labelKey: "tabs.activity", shortLabelKey: "tabs.shortActivity" },
];

export function DashboardTabs() {
  const { t } = useTranslation("dashboard");
  const { hasVisibleWidgetsForTab, config } = useDashboardConfigStore();
  const gymId = useUserStore((state) => state.gymId);
  const userId = useUserStore((state) => state.user?.id);
  const scope = `${userId ?? "anonymous"}:${gymId ?? "no-gym"}`;
  const activeTabs = useDashboardPeriodStore((state) => state.activeTabs);
  const setActiveTab = useDashboardPeriodStore((state) => state.setActiveTab);
  const readingContexts = useDashboardPeriodStore((state) => state.readingContexts);
  const setReadingContext = useDashboardPeriodStore((state) => state.setReadingContext);
  const activeTab = activeTabs[scope] ?? "overview";
  const previousTab = useRef(activeTab);

  const saveReadingContext = (tab: DashboardTab) => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>(`[data-dashboard-tab="${tab}"] [data-widget-id]`));
    const visible = elements.find((element) => element.getBoundingClientRect().bottom > 120);
    setReadingContext(scope, tab, {
      widgetId: visible?.dataset.widgetId ?? null,
      scrollY: window.scrollY,
      viewportOffset: visible?.getBoundingClientRect().top ?? 0,
    });
  };

  const changeTab = (tab: DashboardTab) => {
    saveReadingContext(previousTab.current);
    previousTab.current = tab;
    setActiveTab(scope, tab);
  };

  useEffect(() => {
    const context = readingContexts[`${scope}:${activeTab}`];
    if (!context) return;
    let cancelled = false;
    let attempts = 0;
    let frame = 0;
    const restoreWhenReady = () => {
      if (cancelled) return;
      const tabRoot = document.querySelector<HTMLElement>(`[data-dashboard-tab="${activeTab}"]`);
      const target = context.widgetId
        ? tabRoot?.querySelector<HTMLElement>(`[data-widget-id="${context.widgetId}"]`)
        : null;
      if (target) {
        const top = window.scrollY + target.getBoundingClientRect().top - context.viewportOffset;
        window.scrollTo({ top, behavior: "auto" });
        return;
      }
      attempts += 1;
      if (attempts < 30) frame = requestAnimationFrame(restoreWhenReady);
      else window.scrollTo({ top: context.scrollY, behavior: "auto" });
    };
    frame = requestAnimationFrame(restoreWhenReady);
    return () => { cancelled = true; cancelAnimationFrame(frame); };
  }, [activeTab, readingContexts, scope]);

  useEffect(() => () => saveReadingContext(previousTab.current), [scope]);

  // Filter tabs based on visible widgets
  // Include config.widgets in deps to force recalculation when config changes
  const visibleTabs = useMemo(() => {
    return ALL_TABS.filter(tab => hasVisibleWidgetsForTab(tab.id));
  }, [hasVisibleWidgetsForTab, config.widgets]);

  // Auto-switch to first visible tab if current tab becomes invisible
  useEffect(() => {
    const isCurrentTabVisible = visibleTabs.some(tab => tab.id === activeTab);
    if (!isCurrentTabVisible && visibleTabs.length > 0) {
      setActiveTab(scope, visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab, scope, setActiveTab]);

  // If no tabs visible, show empty state
  if (visibleTabs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <BarChart3 className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground mb-2">
          {t("emptyWidgets.title")}
        </h3>
        <p className="text-sm text-muted-foreground max-w-md">
          {t("emptyWidgets.description")}
        </p>
      </div>
    );
  }

  // Dynamic grid columns based on visible tabs count
  const gridColsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
  }[visibleTabs.length] || 'grid-cols-4';

  return (
    <div className="w-full">
      <Tabs value={activeTab} onValueChange={(v) => changeTab(v as DashboardTab)} className="w-full">
        <div className="border-b border-border/70 bg-background pb-2">
          <TabsList className={`grid w-full ${gridColsClass} h-auto rounded-xl bg-muted/60 p-1`}>
            {visibleTabs.map(tab => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex min-w-0 items-center justify-center gap-1 rounded-lg px-1 py-2.5 text-[11px] sm:gap-2 sm:px-4 sm:text-sm"
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{t(tab.labelKey)}</span>
                <span className="sm:hidden">{t(tab.shortLabelKey)}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="mt-3 sm:mt-5">
          <TabsContent value="overview" className="mt-0" data-dashboard-tab="overview">
            <OverviewTab />
          </TabsContent>

          <TabsContent value="performance" className="mt-0" data-dashboard-tab="performance">
            <Suspense fallback={<TabLoading />}>
              <PerformanceTab />
            </Suspense>
          </TabsContent>

          <TabsContent value="finances" className="mt-0" data-dashboard-tab="finances">
            <Suspense fallback={<TabLoading />}>
              <FinancesTab />
            </Suspense>
          </TabsContent>

          <TabsContent value="activity" className="mt-0" data-dashboard-tab="activity">
            <Suspense fallback={<TabLoading />}>
              <ActivityTab />
            </Suspense>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
